import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Scene } from "../scene/Scene";
import { LayerPanel } from "../panels/LayerPanel";
import { WalkHud } from "../scene/WalkHud";
import { InspectorPanel } from "../panels/InspectorPanel";
import { Minimap } from "../panels/Minimap";
import { KpiStrip } from "../panels/KpiStrip";
import { FinderPanel } from "../panels/FinderPanel";
import { SimulationPanel } from "../panels/SimulationPanel";
import { ZonePanel } from "../panels/ZonePanel";
import { EditModeHud } from "../scene/EditModeHud";
import { EmptyState } from "../components/EmptyState";
import { NavIcon } from "../app/NavIcons";
import { api } from "../api/rest";
import { useTelemetryStore } from "../stores/telemetryStore";
import { useLayoutStore } from "../stores/layoutStore";
import { DEMO_SITE, useSiteStore } from "../stores/siteStore";
import { useUIStore } from "../stores/uiStore";
import zonesJson from "../../../infra/seed/zones.json";
import type { SensorInstall } from "../stores/layoutStore";
import gtpWatermark from "../assets/brand/gtp-mono-white.svg";
import styles from "./LivePage.module.css";

export function LivePage() {
  const { t } = useTranslation();
  const setRoster  = useTelemetryStore((s) => s.setRoster);
  const setSensors = useLayoutStore((s) => s.setSensors);
  const activeSiteId = useSiteStore((s) => s.activeSiteId);
  const activeWarehouseId = useSiteStore((s) => s.activeWarehouseId);
  const hasWarehouses = useSiteStore((s) => s.availableWarehouses.length > 0);
  // ADR 318: a failed load is not an empty site — it gets its own empty state.
  const warehousesError = useSiteStore((s) => s.warehousesError);
  const reloadWarehouses = useSiteStore((s) => s.loadAvailableWarehouses);
  const hasRtls = useSiteStore((s) => s.hasCapability("rtls"));
  const hasTelemetry = useSiteStore((s) => s.hasCapability("telemetry"));
  const showMinimap = useUIStore((s) => s.layers.minimap);
  // ADR 278: both inspection surfaces are selection-first — the right panel
  // exists only for a picked asset/pallet, the bottom dock only for a picked
  // zone. An idle /live is just the floor and the operation column.
  const selectedAssetId = useTelemetryStore((s) => s.selectedAssetId);
  const selectedBin = useUIStore((s) => s.selectedBin);
  const selectedZone = useUIStore((s) => s.selectedZone);
  // ADR 204: the KPI strip is RTLS/telemetry data — on an inventory-only site
  // every card is a permanent "—"/"0", so the strip goes away entirely and the
  // left column takes its slot.
  // ADR 363: an inventory-only site earns the strip while a sweep is streaming.
  const liveSweep = useTelemetryStore((s) => s.inventoryActivity !== null);
  const showKpis = hasRtls || hasTelemetry || liveSweep;

  useEffect(() => {
    if (!activeWarehouseId) return;
    api.listAssets(activeSiteId, activeWarehouseId).then(setRoster).catch(() => {});
    api.listSensors(activeSiteId, activeWarehouseId)
      .then(setSensors)
      .catch(() => {
        // Demo/offline-dev fallback ONLY (ADR 204): a real site's sensor fetch
        // failing must not paint the demo seed's hardware into its scene.
        setSensors(activeSiteId === DEMO_SITE ? ((zonesJson as any).sensors as SensorInstall[] ?? []) : []);
      });
  }, [activeSiteId, activeWarehouseId, setRoster, setSensors]);

  // No warehouse to render — show a guiding empty state instead of the demo
  // fallback model with data-less panels (ADR 175). Three cases: the list never
  // arrived (say so, offer a retry — ADR 318), none exists yet (import a map),
  // or several exist but none is selected (pick one).
  if (!activeWarehouseId) {
    const kind = warehousesError ? "loadFailed" : hasWarehouses ? "selectWarehouse" : "noWarehouse";
    return (
      <div className={styles.layout}>
        <div className={styles.canvasWrap}>
          <EmptyState
            icon={<NavIcon name="warehouses" />}
            title={t(`live.empty.${kind}.title`)}
            description={t(`live.empty.${kind}.desc`)}
            action={
              kind === "loadFailed"
                ? { onClick: () => void reloadWarehouses(activeSiteId), label: t("live.empty.loadFailed.cta") }
                : kind === "noWarehouse"
                  ? { to: "/admin/warehouses", label: t("live.empty.noWarehouse.cta") }
                  : undefined
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout} style={{ position: "relative" }}>
      <div className={styles.canvasWrap}>
        <Scene />
      </div>
      {/* ADR 208: subtle GTP brand watermark, bottom-right, behind the panels. */}
      <img className={styles.watermark} src={gtpWatermark} alt="" aria-hidden="true" />
      {showKpis && <div className={styles.kpis}><KpiStrip /></div>}
      {/* ADR 203: the finder lives in the left column now, not a top-center bar.
          ADR 206: both cards live in one scroll container so a short viewport
          scrolls the whole column — the finder is not pinned above a cramped
          layer-panel window, and the bottom controls stay reachable. */}
      <div className={showKpis ? styles.left : `${styles.left} ${styles.leftFull}`}>
        <div className={styles.leftScroll}><FinderPanel /><SimulationPanel /><LayerPanel /></div>
      </div>
      {(selectedAssetId || selectedBin) && (
        <div className={styles.right}><InspectorPanel /></div>
      )}
      {/* ADR 278: zone inspection lives at the bottom, between the operation
          column and the minimap tile — the third side of the frame around the
          floor (ADR 359: [dock | 2D map | logo], nothing moves). */}
      {selectedZone && (
        <div className={styles.bottom}>
          <ZonePanel siteId={activeSiteId} warehouseId={activeWarehouseId} />
        </div>
      )}
      {showMinimap && (
        <div className={styles.minimap}>
          <Minimap />
        </div>
      )}
      <EditModeHud />
      <WalkHud />
    </div>
  );
}
